<template>
  <div class="notes-field">
    <v-card outlined>
      <v-card-title class="d-flex align-center py-2">
        <span class="text-subtitle-1">{{ label }}</span>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          size="small"
          prepend-icon="mdi-plus"
          @click="openAddDialog"
          :disabled="readonly"
        >
          {{ $t('notes.addNote') }}
        </v-btn>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-card-text class="pa-0">
        <v-list v-if="notes && notes.length > 0" class="py-0" style="max-height: 300px; overflow-y: auto;">
          <v-list-item
            v-for="(note, index) in sortedNotes"
            :key="index"
            class="border-b"
          >
            <template v-slot:prepend>
              <v-avatar :color="getStatusColor(note.statusId)" size="32" class="mr-2">
                <span class="text-white text-caption">{{ getStatusIcon(note.statusId) }}</span>
              </v-avatar>
            </template>
            
            <v-list-item-title class="text-body-2" style="white-space: normal; overflow-wrap: break-word;">
              {{ note.text }}
            </v-list-item-title>
            
            <v-list-item-subtitle class="text-caption mt-1">
              <v-chip size="x-small" :color="getStatusColor(note.statusId)" variant="flat" class="mr-2">
                {{ getStatusDescription(note.statusId) }}
              </v-chip>
              <v-icon size="x-small">mdi-account</v-icon>
              {{ note.user }}
              <v-icon size="x-small" class="ml-2">mdi-clock-outline</v-icon>
              {{ formatDate(note.timestamp) }}
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
        
        <v-alert v-else type="info" variant="tonal" density="compact" class="ma-3">
          {{ $t('notes.noNotes') }}
        </v-alert>
      </v-card-text>
    </v-card>
    
    <!-- Add Note Dialog -->
    <v-dialog v-model="addDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">mdi-note-plus</v-icon>
          {{ $t('notes.addNote') }}
        </v-card-title>
        
        <v-card-text class="pt-4">
          <v-textarea
            v-model="newNoteText"
            :label="$t('notes.noteText')"
            rows="4"
            outlined
            auto-grow
            autofocus
            :rules="[v => !!v || $t('notes.noteRequired')]"
          ></v-textarea>
          
          <v-alert type="info" variant="tonal" density="compact" class="mt-2">
            {{ $t('notes.statusInfo', { status: currentStatusDescription }) }}
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="closeAddDialog"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            @click="addNote"
            :disabled="!newNoteText || newNoteText.trim() === ''"
          >
            {{ $t('common.add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useProjectStore } from '../stores/projects.js'
import { useAuthStore } from '../stores/auth.js'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const projectStore = useProjectStore()
const authStore = useAuthStore()

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  label: {
    type: String,
    required: true
  },
  readonly: {
    type: Boolean,
    default: false
  },
  currentStatus: {
    type: [String, Number],
    required: true
  },
  projectType: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['update:modelValue'])

const notes = computed({
  get: () => props.modelValue || [],
  set: (value) => emit('update:modelValue', value)
})

const addDialog = ref(false)
const newNoteText = ref('')

// Sort notes by timestamp (most recent first)
const sortedNotes = computed(() => {
  return [...notes.value].sort((a, b) => {
    return new Date(b.timestamp) - new Date(a.timestamp)
  })
})

const currentStatusId = computed(() => {
  return parseInt(props.currentStatus)
})

const currentStatusInfo = computed(() => {
  const statusId = String(currentStatusId.value)
  const projectTypeId = parseInt(props.projectType)
  return projectStore.getStatusInfo(statusId, projectTypeId)
})

const currentStatusDescription = computed(() => {
  return currentStatusInfo.value?.descr || `Status ${currentStatusId.value}`
})

const openAddDialog = () => {
  newNoteText.value = ''
  addDialog.value = true
}

const closeAddDialog = () => {
  addDialog.value = false
  newNoteText.value = ''
}

const addNote = () => {
  if (!newNoteText.value || newNoteText.value.trim() === '') return
  
  const note = {
    text: newNoteText.value.trim(),
    statusId: currentStatusId.value,
    statusDescription: currentStatusDescription.value,
    timestamp: new Date().toISOString(),
    user: authStore.user?.username || 'system'
  }
  
  notes.value = [...notes.value, note]
  closeAddDialog()
}

const getStatusColor = (statusId) => {
  const projectTypeId = parseInt(props.projectType)
  const status = projectStore.getStatusInfo(String(statusId), projectTypeId)
  return status?.color || 'grey'
}

const getStatusIcon = (statusId) => {
  const projectTypeId = parseInt(props.projectType)
  const status = projectStore.getStatusInfo(String(statusId), projectTypeId)
  return status?.icon || '📝'
}

const getStatusDescription = (statusId) => {
  const projectTypeId = parseInt(props.projectType)
  const status = projectStore.getStatusInfo(String(statusId), projectTypeId)
  return status?.descr || `Status ${statusId}`
}

const formatDate = (timestamp) => {
  const date = new Date(timestamp)
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
</script>

<style scoped>
.notes-field {
  width: 100%;
}

.border-b {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.border-b:last-child {
  border-bottom: none;
}
</style>
